import { useState, useEffect } from 'react'
import { getCachedImageUrl } from '@renderer/utils/imageCacheUtils'

/**
 * React hook that automatically caches and returns a blob URL for a background image
 * @param imageUrl - The image URL to cache (for use in background-image CSS)
 * @param options - Optional configuration
 * @param options.expirationHours - Override default expiration (24 hours)
 * @returns The cached blob URL or original URL if caching fails
 */
export const useCachedBackgroundImage = (
  imageUrl: string | undefined | null,
  options?: { expirationHours?: number }
): string => {
  const [cachedUrl, setCachedUrl] = useState<string>(imageUrl || '')

  useEffect(() => {
    if (!imageUrl) {
      setCachedUrl('')
      return
    }

    // If URL shouldn't be cached (data URL, blob URL, etc.), use as-is
    if (
      imageUrl.startsWith('data:') ||
      imageUrl.startsWith('blob:') ||
      (!imageUrl.startsWith('http://') &&
        !imageUrl.startsWith('https://') &&
        !imageUrl.startsWith('/'))
    ) {
      setCachedUrl(imageUrl)
      return
    }

    // Fetch and cache the image
    getCachedImageUrl(imageUrl, options)
      .then((url) => {
        setCachedUrl(url)
      })
      .catch((error) => {
        console.error('Error caching background image:', error)
        // Fallback to original URL on error
        setCachedUrl(imageUrl)
      })
  }, [imageUrl, options?.expirationHours])

  return cachedUrl
}
